import { message } from 'antd'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useRef, useState } from 'react'
import {
  applyOepDocument,
  deleteOepDocument,
  getContributionFileDownloadUrl,
  getErrorFileDownloadUrl,
  getOepDocumentDownloadUrl,
} from 'modules/annual-benefit-planning/modules/approval/infra/api/config.api'
import { APPROVAL_QUERY_KEYS } from 'modules/annual-benefit-planning/modules/approval/infra/react-query/approval.query-keys'

interface Options {
  readonly clientId: string
}

interface Result {
  // The documentId currently mid-action ('delete' | 'apply' | 'download' |
  // 'download-contribution' | 'download-error'), or null when idle — a row's
  // action controls disable off this while a request for that row is in flight.
  readonly busy: {
    readonly documentId: string
    readonly action:
      | 'delete'
      | 'apply'
      | 'download'
      | 'download-contribution'
      | 'download-error'
  } | null
  readonly handleDelete: (documentId: string) => Promise<void>
  readonly handleApply: (documentId: string) => Promise<void>
  readonly handleDownload: (documentId: string) => Promise<void>
  readonly handleDownloadContribution: (documentId: string) => Promise<void>
  readonly handleDownloadError: (documentId: string) => Promise<void>
}

/**
 * Per-row Delete/Apply/Download/DownloadContribution actions for the
 * Analysis Files table (EMS-10154 FR-013, Apply restored by EMS-11022).
 * Delete and Apply mutate a single document rather than the whole list, so
 * on success they invalidate the
 * table's own list query plus the client-scoped approval document cache
 * (mirrors the invalidation guarantee `useOepDocumentUpload` already
 * provides for uploads). Downloads are read-only — each opens a
 * presigned S3 URL in a new tab (same pattern as
 * ApprovalSummaryCard/OpenEnrollmentApprovalContainer) and invalidates
 * nothing.
 */
export function useOepDocumentRowActions({ clientId }: Options): Result {
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState<Result['busy']>(null)
  // `busy` only disables Apply after a re-render, so a fast double-click
  // would send two requests; this blocks the second one at once (EMS-11070).
  const isApplyingRef = useRef(false)

  const invalidateAfterMutation = useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: APPROVAL_QUERY_KEYS.oepDocumentList(clientId),
        }),
        queryClient.invalidateQueries({
          queryKey: APPROVAL_QUERY_KEYS.oepDocumentByClient(clientId),
        }),
      ]),
    [queryClient, clientId]
  )

  const handleDelete = useCallback(
    async (documentId: string) => {
      setBusy({ documentId, action: 'delete' })
      try {
        await deleteOepDocument(clientId, documentId)
        void invalidateAfterMutation()
      } catch (error) {
        message.error('Delete failed. Please try again.')
        throw error
      } finally {
        setBusy(null)
      }
    },
    [clientId, invalidateAfterMutation]
  )

  // Makes the file the current one for its plan year. The approval document
  // cache is invalidated too, so the Approval tab shows it without a refresh.
  const handleApply = useCallback(
    async (documentId: string) => {
      if (isApplyingRef.current) return
      isApplyingRef.current = true
      setBusy({ documentId, action: 'apply' })
      try {
        await applyOepDocument(clientId, documentId)
      } catch {
        message.error('Apply failed. Please try again.')
        isApplyingRef.current = false
        setBusy(null)
        return
      }
      // Stay busy until the list shows the new current file, so Apply can't
      // be clicked again against the old status. The ABP 5 jobs list carries
      // the generated files' current flag (EMS-11070).
      await Promise.allSettled([
        invalidateAfterMutation(),
        queryClient.invalidateQueries({
          queryKey: APPROVAL_QUERY_KEYS.abp5FileJobsByDocument(clientId),
        }),
      ])
      message.success('Applied.')
      isApplyingRef.current = false
      setBusy(null)
    },
    [clientId, invalidateAfterMutation, queryClient]
  )

  const handleDownload = useCallback(
    async (documentId: string) => {
      setBusy({ documentId, action: 'download' })
      try {
        const { presignedUrl } = await getOepDocumentDownloadUrl(
          clientId,
          documentId
        )
        // The presigned URL carries a Content-Disposition: attachment header
        // (set server-side), so opening it downloads the file directly.
        window.open(presignedUrl, '_blank', 'noopener,noreferrer')
      } catch {
        message.error('Download failed. Please try again.')
      } finally {
        setBusy(null)
      }
    },
    [clientId]
  )

  const handleDownloadContribution = useCallback(
    async (documentId: string) => {
      setBusy({ documentId, action: 'download-contribution' })
      try {
        const { presignedUrl } = await getContributionFileDownloadUrl(
          clientId,
          documentId
        )
        window.open(presignedUrl, '_blank', 'noopener,noreferrer')
      } catch {
        message.error('Download failed. Please try again.')
      } finally {
        setBusy(null)
      }
    },
    [clientId]
  )

  const handleDownloadError = useCallback(
    async (documentId: string) => {
      setBusy({ documentId, action: 'download-error' })
      try {
        const { presignedUrl } = await getErrorFileDownloadUrl(
          clientId,
          documentId
        )
        window.open(presignedUrl, '_blank', 'noopener,noreferrer')
      } catch {
        message.error('Download failed. Please try again.')
      } finally {
        setBusy(null)
      }
    },
    [clientId]
  )

  return {
    busy,
    handleDelete,
    handleApply,
    handleDownload,
    handleDownloadContribution,
    handleDownloadError,
  }
}
